// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// Pure geometry for the hand-rolled charts (histogram binning, Sankey node
// stacking). Kept dependency-free and separate from rendering so the positions
// can be unit-tested — area-only checks pass on visually broken layouts.

export interface Bin { x0: number; x1: number; idx: number[]; count: number }

/**
 * Bin `values` into `binCount` equal-width buckets over [min, max]. Each value's
 * index is recorded so a bar click can filter back to the source rows. Values
 * exactly at `max` land in the last bin (not a phantom overflow bin).
 */
export function binValues(values: number[], min: number, max: number, binCount: number): Bin[] {
  const span = max - min || 1;
  const w = span / binCount;
  const bins: Bin[] = Array.from({ length: binCount }, (_, i) => ({
    x0: min + i * w, x1: min + (i + 1) * w, idx: [], count: 0,
  }));
  values.forEach((v, i) => {
    if (!Number.isFinite(v)) return;
    let b = Math.floor((v - min) / w);
    if (b < 0) b = 0;
    if (b >= binCount) b = binCount - 1;
    bins[b].idx.push(i);
    bins[b].count++;
  });
  return bins;
}

export interface Band { y: number; h: number }

/**
 * Stack `values` as vertical bands filling [0, height] minus (n-1) gaps, each
 * band's height proportional to its value. Returns y/height for each band in
 * input order. Used for both sides of a Sankey column.
 */
export function stackBands(values: number[], height: number, gap: number): Band[] {
  const n = values.length;
  if (n === 0) return [];
  const total = values.reduce((a, b) => a + Math.max(0, b), 0) || 1;
  const avail = Math.max(0, height - gap * (n - 1));
  const bands: Band[] = [];
  let y = 0;
  for (let i = 0; i < n; i++) {
    const h = (Math.max(0, values[i]) / total) * avail;
    bands.push({ y, h });
    y += h + gap;
  }
  return bands;
}

/** Nice rounded axis maximum for a set of counts (e.g. 47 -> 50). */
export function niceMax(v: number): number {
  if (v <= 0) return 1;
  const mag = Math.pow(10, Math.floor(Math.log10(v)));
  const n = v / mag;
  const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
  return step * mag;
}
